import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";

// Anti-bot form guard: server-issued timed token + hidden honeypot ("website") + invisible Cloudflare Turnstile ("cf").
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__tbTurnstileReady";
const TOKEN_WAIT_MS = 6000;
let turnstilePromise = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstilePromise) {
    turnstilePromise = new Promise((resolve, reject) => {
      window.__tbTurnstileReady = () => resolve(window.turnstile);
      const s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onerror = () => { turnstilePromise = null; reject(new Error("turnstile script")); };
      document.head.appendChild(s);
    });
  }
  return turnstilePromise;
}

export function useFormGuard(active = true) {
  const [token, setToken] = useState("");
  const [siteKey, setSiteKey] = useState("");
  const [containerReady, setContainerReady] = useState(false);
  const [cfError, setCfError] = useState("");
  const hp = useRef("");
  const cf = useRef("");
  const siteKeyRef = useRef("");
  const container = useRef(null);
  const widget = useRef(null);
  const waiters = useRef([]);

  const refresh = useCallback(() => {
    api.get("/forms/token").then((r) => {
      setToken(r.data.token);
      siteKeyRef.current = r.data.turnstile_site_key || "";
      setSiteKey(siteKeyRef.current);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (active) refresh();
  }, [active, refresh]);

  useEffect(() => {
    if (!siteKey || !containerReady || widget.current || !container.current) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !container.current || widget.current) return;
      widget.current = ts.render(container.current, {
        sitekey: siteKey,
        action: "form",
        "response-field": false,
        "refresh-expired": "auto",
        callback: (t) => { cf.current = t; setCfError(""); waiters.current.splice(0).forEach((w) => w(t)); },
        "expired-callback": () => { cf.current = ""; },
        "error-callback": (code) => { cf.current = ""; setCfError(String(code || "error")); console.warn("Turnstile error", code, window.location.hostname); return true; },
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) { try { window.turnstile.remove(widget.current); } catch { /* ignore */ } }
      widget.current = null;
    };
  }, [siteKey, containerReady]);

  const cfToken = () => new Promise((resolve) => {
    if (!siteKeyRef.current || cf.current) return resolve(cf.current);
    const timer = setTimeout(() => { waiters.current = waiters.current.filter((w) => w !== done); resolve(""); }, TOKEN_WAIT_MS);
    const done = (t) => { clearTimeout(timer); resolve(t); };
    waiters.current.push(done);
    if (widget.current && window.turnstile && !cf.current) { try { window.turnstile.reset(widget.current); } catch { /* ignore */ } }
  });

  const consume = () => {
    cf.current = "";
    if (widget.current && window.turnstile) { try { window.turnstile.reset(widget.current); } catch { /* ignore */ } }
  };

  const guard = async () => {
    const t = await cfToken();
    consume();
    return { ft: token, website: hp.current, cf: t };
  };
  const appendTo = async (fd) => {
    const g = await guard();
    fd.append("ft", g.ft); fd.append("website", g.website); fd.append("cf", g.cf);
  };

  const setContainer = useCallback((el) => { container.current = el; setContainerReady(!!el); }, []);

  const Honeypot = useCallback(() => (
    <>
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="website-field">Website</label>
        <input id="website-field" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" onChange={(e) => { hp.current = e.target.value; }} />
      </div>
      <div ref={setContainer} data-testid="turnstile-container" className="empty:hidden" />
    </>
  ), [setContainer]);

  const GuardNotice = useCallback(() => (siteKeyRef.current ? <TurnstileNotice error={cfError} /> : null), [cfError]);

  return { guard, appendTo, refresh, Honeypot, GuardNotice };
}

function TurnstileNotice({ error }) {
  const { t } = useT();
  const g = t.guard;
  return (
    <>
    {error && (
      <p data-testid="turnstile-error" className="text-xs text-brand-red leading-relaxed">
        {(error.startsWith("1102") ? g.error : g.error_generic).replace("{host}", window.location.hostname).replace("{code}", error)}
      </p>
    )}
    <p data-testid="turnstile-notice" className="text-[11px] leading-relaxed text-muted-foreground">
      {g.notice}{" "}
      <a href="https://www.cloudflare.com/turnstile-privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground">{g.privacy}</a>
      {" · "}
      <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground">{g.terms}</a>
    </p>
    </>
  );
}
